import React, { useMemo } from "react";
import {
  Battery,
  Calendar,
  Gauge,
  ShieldCheck,
  AlertTriangle,
  Zap,
  Info,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  Tooltip,
  Area,
  AreaChart,
} from "recharts";
import { predictLifespan } from "../../lib/lifespanPredictor";

const MS_PER_MONTH = 30.44 * 24 * 60 * 60 * 1000;

/**
 * BatteryLifePredictor — dedicated card that turns EVIQ's degradation math
 * into a clear lifespan forecast. Shows total years of usable life, projected
 * end-of-life date, milestone timeline (80/70/60), and a plain-English
 * narrative. This is the "years of your pack's life" answer that competitor
 * apps don't attempt.
 */
export const BatteryLifePredictor = ({
  currentSoh,
  monthlyLoss,
  climateZone,
  vehicleAgeYears,
  milesPerYear = 12000,
  warrantyThreshold = 70,
  warrantyYears = 8,
  warrantyMiles = 120000,
  dataMonths = 6,
  brandName = "Tesla",
}) => {
  const result = useMemo(
    () =>
      predictLifespan({
        currentSoh,
        monthlyLoss,
        climateZone,
        vehicleAgeYears,
        milesPerYear,
        warrantyThreshold,
        warrantyYears,
        warrantyMiles,
        dataMonths,
        brandName,
      }),
    [
      currentSoh,
      monthlyLoss,
      climateZone,
      vehicleAgeYears,
      milesPerYear,
      warrantyThreshold,
      warrantyYears,
      warrantyMiles,
      dataMonths,
      brandName,
    ]
  );

  // Build a smooth SoH decay curve for the chart, monthly for 15 years.
  const chartData = useMemo(() => {
    const points = [];
    const now = new Date();
    for (let m = 0; m <= 180; m += 3) {
      const soh = Math.max(50, currentSoh - result.effectiveMonthlyLoss * m);
      const d = new Date(now.getTime() + m * MS_PER_MONTH);
      points.push({
        year: +(m / 12).toFixed(1),
        soh: +soh.toFixed(2),
        label: d.toLocaleDateString("en-US", { year: "numeric", month: "short" }),
      });
      if (soh <= result.endOfLifeThreshold - 2) break;
    }
    return points;
  }, [currentSoh, result]);

  const confidenceBadge = {
    high: { color: "#39FF14", label: "HIGH CONFIDENCE" },
    medium: { color: "#00E5FF", label: "MED CONFIDENCE" },
    low: { color: "#FFB020", label: "EARLY-DAYS" },
  }[result.confidence];

  return (
    <div
      data-testid="battery-life-predictor"
      className="rounded-2xl border p-4 sm:p-5 bg-gradient-to-br from-[#00E5FF]/[0.03] to-transparent"
      style={{ borderColor: "rgba(0,229,255,0.25)" }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 border"
              style={{
                background: "rgba(0,229,255,0.1)",
                borderColor: "rgba(0,229,255,0.35)",
              }}
            >
              <Battery className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2.2} />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Battery Life Predictor
            </div>
          </div>
          <div className="text-white text-lg font-semibold leading-tight">
            Roughly{" "}
            <span
              className="font-display"
              style={{ color: "#00E5FF" }}
              data-testid="life-predictor-years"
            >
              {result.yearsRemaining} yrs
            </span>{" "}
            of practical life left.
          </div>
        </div>
        <span
          className="shrink-0 font-mono text-[9px] uppercase tracking-wider px-1.5 py-1 rounded border"
          style={{
            color: confidenceBadge.color,
            borderColor: `${confidenceBadge.color}55`,
            background: `${confidenceBadge.color}12`,
          }}
          data-testid="life-predictor-confidence"
        >
          {confidenceBadge.label}
        </span>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatTile
          label="End-of-life"
          value={result.endOfLifeDateLabel}
          icon={Calendar}
          testid="life-predictor-eol-date"
        />
        <StatTile
          label="Miles left"
          value={`${(result.milesRemaining / 1000).toFixed(0)}k`}
          sub="at today's pace"
          icon={Gauge}
          testid="life-predictor-miles-left"
        />
        <StatTile
          label={result.willQualifyForWarranty ? "Warranty ETA" : "70% ETA"}
          value={`${result.yearsToWarranty} yrs`}
          sub={
            result.willQualifyForWarranty
              ? "claim-eligible"
              : result.yearsToWarranty > result.yearsUntilWarrantyEnds
              ? "aging slower than window"
              : "beyond window"
          }
          icon={result.willQualifyForWarranty ? ShieldCheck : AlertTriangle}
          tint={result.willQualifyForWarranty ? "#39FF14" : "#00E5FF"}
          testid="life-predictor-warranty"
        />
      </div>

      {/* SoH decay chart */}
      <div className="mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
          Projected State-of-Health over time
        </div>
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="sohLifeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="year"
                stroke="#3f3f46"
                tick={{ fill: "#71717a", fontSize: 9, fontFamily: "monospace" }}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                tickLine={false}
                unit="y"
              />
              <YAxis
                domain={[50, 100]}
                stroke="#3f3f46"
                tick={{ fill: "#71717a", fontSize: 9, fontFamily: "monospace" }}
                axisLine={false}
                tickLine={false}
                unit="%"
                width={30}
              />
              <ReferenceLine
                y={80}
                stroke="rgba(57,255,20,0.35)"
                strokeDasharray="2 3"
                label={{ value: "80%", position: "insideTopRight", fill: "#39FF14", fontSize: 9, fontFamily: "monospace" }}
              />
              <ReferenceLine
                y={70}
                stroke="rgba(255,176,32,0.4)"
                strokeDasharray="3 3"
                label={{ value: "70% warranty", position: "insideTopRight", fill: "#FFB020", fontSize: 9, fontFamily: "monospace" }}
              />
              <ReferenceLine
                y={60}
                stroke="rgba(255,107,87,0.4)"
                strokeDasharray="3 3"
                label={{ value: "60% EOL", position: "insideBottomRight", fill: "#FF6B57", fontSize: 9, fontFamily: "monospace" }}
              />
              <Tooltip
                contentStyle={{
                  background: "rgba(10,10,10,0.95)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 8,
                  fontSize: 11,
                  fontFamily: "monospace",
                }}
                labelStyle={{ color: "#a1a1aa", fontSize: 10 }}
                itemStyle={{ color: "white" }}
                formatter={(v) => [`${v}%`, "SoH"]}
                labelFormatter={(v) => `${v} years from now`}
              />
              <Area
                type="monotone"
                dataKey="soh"
                stroke="#00E5FF"
                strokeWidth={2}
                fill="url(#sohLifeGrad)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Milestone timeline */}
      <div className="mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
          Milestones ahead
        </div>
        <ol className="space-y-2">
          {result.milestones.map((m) => (
            <MilestoneRow key={m.threshold} milestone={m} />
          ))}
        </ol>
      </div>

      {/* Narrative */}
      <div
        className="rounded-xl border p-3 flex items-start gap-2 text-[12px] text-zinc-300 leading-relaxed"
        style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
        data-testid="life-predictor-narrative"
      >
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-zinc-500" strokeWidth={2} />
        <div>{result.narrative}</div>
      </div>
    </div>
  );
};

const StatTile = ({ label, value, sub, icon: Icon, tint = "#00E5FF", testid }) => (
  <div
    className="rounded-xl border border-white/8 bg-black/30 px-3 py-2.5 min-w-0"
    data-testid={testid}
  >
    <div className="flex items-center gap-1 mb-1 min-w-0">
      <Icon className="w-3 h-3 shrink-0" style={{ color: tint }} strokeWidth={2.2} />
      <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 truncate">
        {label}
      </span>
    </div>
    <div className="text-white text-sm font-semibold tracking-tight truncate">
      {value}
    </div>
    {sub && (
      <div className="font-mono text-[9px] text-zinc-500 mt-0.5 truncate">
        {sub}
      </div>
    )}
  </div>
);

const MilestoneRow = ({ milestone }) => {
  const color =
    milestone.severity === "critical"
      ? "#FF6B57"
      : milestone.severity === "warn"
      ? "#FFB020"
      : "#39FF14";
  const Icon = milestone.severity === "critical" ? Zap : milestone.severity === "warn" ? ShieldCheck : Battery;
  return (
    <li
      className="flex items-center gap-3 rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2"
      data-testid={`life-predictor-milestone-${milestone.threshold}`}
    >
      <div
        className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 border"
        style={{ background: `${color}12`, borderColor: `${color}55` }}
      >
        <Icon className="w-3 h-3" style={{ color }} strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-white font-medium leading-tight">
          {milestone.threshold}% SoH ·{" "}
          <span className="text-zinc-400 font-normal">{milestone.event}</span>
        </div>
        <div className="font-mono text-[10px] text-zinc-500 leading-tight mt-0.5">
          {milestone.already
            ? "already crossed"
            : `${milestone.yearsAway} yrs → ${milestone.dateLabel}`}
        </div>
      </div>
    </li>
  );
};
